import React, {useCallback, useEffect, useState} from 'react'
import { getCurrentUser } from '../utils/auth'
import {format} from 'date-fns'
import "./Users.css"
import { useNavigate } from "react-router-dom"
import { Link } from 'react-router-dom'
import { BsFillPencilFill, BsFillTrashFill } from 'react-icons/bs'
import ModalUserEdit from "./ModalUserEdit"
import ModalUserDelete from './ModalUserDelete'
import Caret from './Icon/Caret'

const Users = ({deleteRow}) => {
    const tokenPayload = getCurrentUser() || {};
    const tokenCustomerId = tokenPayload?.customer;
    const userScopes = tokenPayload?.scopes || [];
    const navigate = useNavigate();
    const permissionScope = 'userData'
    const hasGlobalReadPermission = userScopes.includes('read:' + permissionScope);
    const hasCustomerScopedReadPermission = userScopes.some((scope) => scope.startsWith('read:' + permissionScope + 'Customer'));
    const shouldFilterByCustomer = Boolean(tokenCustomerId !== undefined && tokenCustomerId !== null && hasCustomerScopedReadPermission && !hasGlobalReadPermission);
    const fetchAllUsersUrl = process.env.REACT_APP_API_URL_BASE + '/users/view'
    const fetchCustomerUsersUrl = process.env.REACT_APP_API_URL_BASE + '/users/customer/*id*/view'
    const fetchSingleUserUrl = process.env.REACT_APP_API_URL_BASE + '/users/user/*id*/view'
    const fieldsNoSort = ["Info", "Active", "Authorized", "Actions"]
    const headers = [
        {
            id: 0,
            KEY: "info",
            LABEL: "Info",
            CLASS: "icons-width"
        },
        {
            id: 1,
            KEY: "web_user_id",
            LABEL: "ID",
            CLASS: "left"
        },
        {
            id: 2,
            KEY: "last_name",
            LABEL: "Last Name",
            CLASS: "left"
        },
        {
            id: 3,
            KEY: "first_name",
            LABEL: "First Name",
            CLASS: "left"
        },
        {
            id: 4,
            KEY: "username",
            LABEL: "Username",
            CLASS: "left"
        },
        {
            id: 5,
            KEY: "email",
            LABEL: "Email",
            CLASS: ""
        },
        {
            id: 6,
            KEY: "description",
            LABEL: "User Type",
            CLASS: "left"
        },
        {
            id: 7,
            KEY: "customer_name",
            LABEL: "Customer",
            CLASS: "left"
        },
        {
            id: 8,
            KEY: "is_active",
            LABEL: "Active",
            CLASS: "left"
        },
        {
            id: 9,
            KEY: "created_on",
            LABEL: "Created On",
            CLASS: "left"
        },
        {
            id: 10,
            KEY: "last_login",
            LABEL: "Last Sign In",
            CLASS: "left"
        },
        {
            id: 11,
            KEY: "actions",
            LABEL: "Actions",
            CLASS: "left"
        }
    ]

    const [userEdit, setUserEdit] = useState(null);
    const [allUserTypes, setAllUserTypes] = useState(null);
    const [allCustomers, setAllCustomers] = useState([]);
    const [editedRecord, setEditedRecord] = useState(null);
    const [deletedRecord, setDeletedRecord] = useState(null);
    const [isNewUser, setIsNewUser] = useState(false);
    const [modalEdit, setModalEdit] = useState(false);
    const [modalDelete, setModalDelete] = useState(false);
    const [isReadOnlyModal, setIsReadOnlyModal] = useState(false);
    const [records, setRecords] = useState([]);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [showNotActive, setShowNotActive] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [sort, setSort] = useState({ keyToSort: "last_name", direction: "asc"});

    const hasReadPermission = hasGlobalReadPermission || hasCustomerScopedReadPermission;
    const canSoftDeactivateUser = userScopes.some((scope) =>
        scope.startsWith('delete:' + permissionScope) || scope.startsWith('update:' + permissionScope)
    );
    const canShowNotActiveToggle = canSoftDeactivateUser;

    const normalizedSearchText = searchText.trim().toLowerCase();

    const matchesSearch = (record) => {
        if (!normalizedSearchText) {
            return true;
        }

        const searchableValues = [
            record.id,
            record.first_name,
            record.last_name,
            record.username,
            record.email,
            record.description,
            record.customer_name,
        ];

        return searchableValues
            .filter((value) => value !== null && value !== undefined)
            .some((value) => String(value).toLowerCase().includes(normalizedSearchText));
    }

    const visibleRecords = records
        .map((record, recordIndex) => ({ record, recordIndex }))
        .filter(({ record }) => (showNotActive || record.is_active === true) && matchesSearch(record));


    const fetchAllUsersData = useCallback(async () => {
        setIsTableLoading(true);
        try{
            const response = await fetch(fetchAllUsersUrl,
                {
                    method: 'GET',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    },
                });
            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }
            const json = await response.json();
            setRecords(json);
        } catch (error) {
            console.error(error);
        } finally {
            setIsTableLoading(false);
        }
    }, [fetchAllUsersUrl])

    const fetchCustomerUserData = useCallback(async (customer_id) => {
        setIsTableLoading(true);
        try{
            const response = await fetch(fetchCustomerUsersUrl.replace("*id*", customer_id),
                {
                    method: 'GET',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    },
                });
            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }
            const json = await response.json();
            setRecords(json);
        } catch (error) {
            console.error(error);
        } finally {
            setIsTableLoading(false);
        }
    }, [fetchCustomerUsersUrl])

    const fetchSingleUserData = async (user_id) => {
        try{
            const response = await fetch(fetchSingleUserUrl.replace("*id*", user_id),
                {
                    method: 'GET',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    },
                });
            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }
            const json = await response.json();
            setRecords(originalRecords => {
                const newRecords = [...originalRecords];
                newRecords[editedRecord] = json[0];
                return newRecords;
            });
        } catch (error) {
            console.error(error);
        }
    }

    const fetchAllUserTypes = async () => {
        fetch(process.env.REACT_APP_API_URL_BASE + '/user_types/',
        {
            method: 'GET',
            headers: {
                'machine':'2',
                'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
            },
        })
        .then(res => res.json())
        .then(data => {
            const userTypes = data.map(item => ({ description: item.description, id: item.user_type_id }));
            setAllUserTypes(userTypes)
        })
        .catch(error => console.error(error));
    }

    const fetchAllCustomers = async () => {
            fetch(process.env.REACT_APP_API_URL_BASE + '/customers/',
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then(res => res.json())
            .then(data => {
                const formattedCustomers = data.map(item => ({ customer_name: item.customer_name, id: item.customer_id }));
                setAllCustomers(formattedCustomers)
            })
            .catch(error => console.error(error));
        }

    useEffect(() => {
        if (!window.sessionStorage.getItem("auth") || !hasReadPermission) {
            navigate('/unauthorized')
        }

        if (shouldFilterByCustomer) {
            //pull only users attached to this users customer ID
            fetchCustomerUserData(tokenCustomerId);
        }
        else {
            // pull all Facilities
            fetchAllUsersData();
        }
        fetchAllUserTypes();
        fetchAllCustomers();
    }, [fetchAllUsersData, fetchCustomerUserData, navigate, hasReadPermission, tokenCustomerId, shouldFilterByCustomer]);

    function handleHeaderClick(header) {
        if (!fieldsNoSort.includes(header.LABEL)) {
            const nextDirection =
                header.KEY === sort.keyToSort
                    ? (sort.direction === 'asc' ? 'desc' : 'asc')
                    : 'asc';

            const nextSort = {
                keyToSort: header.KEY,
                direction: nextDirection,
            };

            setSort(nextSort);
            setRecords((originalRecords) => {
                const sortedData = [...originalRecords].sort((a, b) => {
                    const aValue = a[nextSort.keyToSort];
                    const bValue = b[nextSort.keyToSort];

                    if (aValue === null || aValue === undefined) return 1;
                    if (bValue === null || bValue === undefined) return -1;

                    const aComparable = String(aValue).toLowerCase();
                    const bComparable = String(bValue).toLowerCase();

                    if (nextSort.direction === 'asc') {
                        return aComparable.localeCompare(bComparable);
                    }

                    return bComparable.localeCompare(aComparable);
                });

                return sortedData;
            });
        }
    }

    const handleUserEdit = (userInfo, recordIndex) => {
        // edit user info icon clicked
        // set the record in order to do a refresh on the edited user
        setEditedRecord(recordIndex)
        setUserEdit(userInfo)
        setIsReadOnlyModal(false);
        setIsNewUser(false);
        setModalEdit(true);
      };

    const handleUserView = (userInfo, recordIndex, event) => {
        if (event.target.closest('.actions-cell')) {
            return;
        }

        setEditedRecord(recordIndex);
        setUserEdit(userInfo);
        setIsReadOnlyModal(true);
        setModalEdit(true);
    }

    const handleDeleteUser = (userInfo, recordIndex) => {
        if (canSoftDeactivateUser) {
            setDeletedRecord(recordIndex);
            setUserEdit(userInfo);
            setModalDelete(true);
        }
    }

    const handleDeleteConfirm = async () => {
        // Delete icon clicked: set user inactive
        if (canSoftDeactivateUser && deletedRecord !== null && userEdit?.id) {
            try {
                const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/users/user/' + userEdit.id,
                    {
                        method: 'PATCH',
                        headers: {
                            'machine':'2',
                            'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                            'content-type': 'application/json'
                        },
                        body: JSON.stringify({
                            is_active: false
                        }),
                    });

                if (!response.ok) {
                    throw new Error(`Failed to set user inactive. Status: ${response.status}`);
                }

                setRecords((originalRecords) => originalRecords.map((record, index) => {
                    if (index !== deletedRecord) {
                        return record;
                    }

                    return {
                        ...record,
                        is_active: false,
                    };
                }));

                setDeletedRecord(null);
            } catch (error) {
                console.error(error);
            }
        }
    }

    const handleEditSubmit = (newRow) => {
        if (isNewUser) {
            // pull all data - refresh page
            window.location.reload()
            setIsNewUser(false)
        }
        else {
            // edited existing user - update specific row
            fetchSingleUserData(records[editedRecord].id)
            setEditedRecord(null)
        }
      }

    const showDelete = (record, recordIndex) => {
        // if the user can deactivate then show the trash icon
        if (canSoftDeactivateUser) {
            return <BsFillTrashFill onClick={() => handleDeleteUser(record, recordIndex)} className='delete-btn' title='Deactivate this User' aria-label='Deactivate this User'/>
        }
    }

    const showEdit = (record, recordIndex) => {
        // if the user has edit scope then show the edit icon
        if ((tokenPayload.scopes || []).some((scope) =>
            scope.startsWith('update:' + permissionScope) || scope.startsWith('create:' + permissionScope)
        )) {
            return <BsFillPencilFill onClick={() => handleUserEdit(record,recordIndex)} className='edit-btn' title='Edit User' aria-label='Edit User'/>
        }
    }

    const showCreateNew = () => {
        // if the user has create scope then show the Add New User button
        if ((tokenPayload.scopes || []).some((scope) => scope.startsWith('create:' + permissionScope))) {
            return (
                <Link className='btn users-add-btn' to='/register'>+ Add New User</Link>
            )
        }
    }

    function showDebug() {
        // var debugString = 'Sort Direction: ' + sort.direction + ' Sort Field: ' + sort.keyToSort;
        // // var debugString = sort.keyToSort === 'last_name' ? sort.direction : "asc"
        // return debugString
    }

    return (
        <div className='table-wrapper users-page'>
            <h3 className='page-title'>
                Users <span className='page-count'>{visibleRecords.length}</span>
            </h3>
            <div>
                {showDebug()}
            </div>
            <div>
                {showCreateNew()}
            </div>
            <div className='users-filter'>
                <div className='table-search-input-wrap'>
                    <input
                        type='text'
                        className='users-search-input table-search-input'
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        placeholder='Search users'
                        aria-label='Search users'
                    />
                    <button
                        type='button'
                        className='table-search-clear-icon'
                        onClick={() => setSearchText('')}
                        disabled={!searchText}
                        aria-label='Clear user search'
                    >
                        x
                    </button>
                </div>
                {canShowNotActiveToggle && (
                    <label className='users-filter-label'>
                        <input
                            type='checkbox'
                            checked={showNotActive}
                            onChange={(event) => setShowNotActive(event.target.checked)}
                        />
                        Show Not Active
                    </label>
                )}
            </div>
            <font size="3">
            <table className='table'>
                <thead>
                    <tr>
                        {headers.map((header, index) => (
                            <th key={index} className={header.CLASS} onClick={() => handleHeaderClick(header)}>
                                {header.LABEL}
                                {header.KEY === sort.keyToSort && (
                                <Caret direction={sort.keyToSort === header.KEY ? sort.direction : "asc"}/>)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {
                        isTableLoading ? (
                            Array.from({ length: 6 }).map((_, rowIndex) => (
                                <tr key={`users-skeleton-${rowIndex}`} className='table-skeleton-row'>
                                    {headers.map((header, cellIndex) => (
                                        <td key={`users-skeleton-${rowIndex}-${header.KEY}-${cellIndex}`}>
                                            <span className={`table-skeleton-line${header.KEY === 'info' || header.KEY === 'actions' ? ' short' : ''}`} />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            visibleRecords.map(({ record, recordIndex }) => {
                                return <tr key={recordIndex} onClick={(event) => handleUserView(record, recordIndex, event)}>
                                <td>
                                    <span className='actions'>

                                    </span>
                                </td>
                                <td className='left'>{record.id}</td>
                                <td className='left'>{record.last_name}</td>
                                <td className='left'>{record.first_name}</td>
                                <td className='left'>{record.username}</td>
                                <td className='left'>{record.email}</td>
                                <td className='left'>{record.description}</td>
                                <td className='left'>{record.customer_name}</td>
                                <td className='center'>{record.is_active ? "\u2714" : ""}</td>
                                <td className='left'>{record.created_on ? format(new Date(record.created_on), 'MMM d, yyyy') : ''}</td>
                                <td className='left'>{record.last_login ? format(new Date(record.last_login), 'MMM d, yyyy') : ''}</td>
                                <td className='actions-cell'>
                                    <span className='actions'>
                                        {showEdit(record, recordIndex)}
                                        {showDelete(record, recordIndex)}
                                    </span>
                                </td>
                            </tr>
                            })
                        )}
                </tbody>
            </table>
            </font>
            {modalEdit && <ModalUserEdit closeModal={() => {
                setModalEdit(false)
                setIsReadOnlyModal(false)
            }}
                onSubmit={handleEditSubmit}
                userEdit={userEdit}
                isReadOnly={isReadOnlyModal}
                // isNewUser={isNewUser}
                allUserTypes={allUserTypes}
                allCustomers={allCustomers}
            />}
            {modalDelete && <ModalUserDelete closeModal={() => {
                setModalDelete(false);
                setDeletedRecord(null);
            }}
                onSubmit={handleDeleteConfirm}
                userInfo={userEdit}
            />}

        </div>
  )
}

export default Users



